import { FC, useState, useContext, useEffect, useCallback } from "react";
import Modal from "@app/shared/Modals/Modal";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDrogMultiFileUploader";
import { SupplementaryDocument } from "@app/projects/results/common/specialtyGroupException";
import { DragAndDropMultiFileUploaderContext, DragAndDropMultiFileUploaderContextProvider } from "@app/shared/DragAndDropMultiFileUploaderContext";
import { toast } from "react-toastify";
import ExceptionsService from "./exceptionsService";
import { QESAlert } from "../shared/qes-standards";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";

interface AddAttachmentModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  selectedExceptionIds: number[];
  resetSelectedCheckboxes: () => void;
}

const WrappedAddAttachmentModal: FC<AddAttachmentModalProps> = ({ isModalOpen, setIsModalOpen, selectedExceptionIds, resetSelectedCheckboxes }) => {
  const [attachments, setAttachments] = useState<SupplementaryDocument[]>([]);
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext);
  const uploadDocumentsApiKey: string = "document";
  const exceptionsService = new ExceptionsService();

  useEffect(() => {
    if (!isModalOpen) {
      resetModalState();
    }
  }, [isModalOpen]);

  const handleAddAttachment = useCallback(async () => {
    if (attachments.length === 0) return;

    try {
      const supplementaryDocuments = selectedExceptionIds.flatMap(specialtyGroupExceptionId =>
        attachments.map(attachment => ({
          documentName: attachment.documentName,
          specialtyGroupExceptionId: specialtyGroupExceptionId,
          supplementaryDocumentMetadataId: attachment.supplementaryDocumentMetadataId,
        }))
      );

      await exceptionsService.addSupplementaryDocumentsToExceptions(supplementaryDocuments);

      toast.success(`Document${attachments.length > 1 ? 's have' : ' has'} been successfully attached!`, {
        containerId: "z-index-1030",
      });

      resetSelectedCheckboxes();

    } catch (error) {
      toast.error("Error adding attachments");
    } finally {
      resetModalState();
      setIsModalOpen(false);
    }
  }, [attachments, selectedExceptionIds, exceptionsService, setIsModalOpen, resetSelectedCheckboxes]);

  const resetModalState = useCallback(() => {
    setAttachments([]);
    multiFileUploaderContext.setUploadedFiles(new Map());
  }, [multiFileUploaderContext]);

  const handleCloseModal = useCallback(() => {
    resetModalState();
    setIsModalOpen(false);
  }, [resetModalState, setIsModalOpen]);

  const handleDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  };

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      const formData = new FormData();
      formData.append(uploadDocumentsApiKey, file);
      const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
      const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);
      if (!!metadataId && fileinfo instanceof File) {
        const supplementaryDocument: SupplementaryDocument = {
          documentName: fileinfo.name ? fileinfo.name : '',
          supplementaryDocumentMetadataId: metadataId,
        };
        setAttachments(prevDocuments => [...prevDocuments, supplementaryDocument]);
        multiFileUploaderContext.setUploadedFiles(prevFiles => {
          const newFiles = new Map(prevFiles);
          newFiles.set(metadataId, file);
          return newFiles;
        });
      }
    } catch (error) {
      throw error.responseJSON;
    }
  }, [exceptionsService, multiFileUploaderContext]);

  const handleFileRemove = useCallback((documentKey: number) => {
    setAttachments(prevDocuments => prevDocuments.filter(item => item.supplementaryDocumentMetadataId != documentKey));
    multiFileUploaderContext.setUploadedFiles(prevFiles => {
      const newFiles = new Map(prevFiles);
      newFiles.delete(documentKey);
      return newFiles;
    });
  }, [multiFileUploaderContext]);

  return (
    <Modal
      closeModal={handleCloseModal}
      content={{
        body: (
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <QESAlert classNames="mb-3" type="info" persist={true}>
              <strong> {selectedExceptionIds.length > 1 ? 'Attachment will be added to all selected exceptions.' :'Attachment will be added to the selected exception.'}</strong>
            </QESAlert>
            <DragAndDropMultiFileUploader
              apiKeyName={uploadDocumentsApiKey}
              title="Attach Supplemental Documents. Attachments must be in .doc, .docx, .png, .jpg, or .pdf format."
              filetype=".doc,.docx,.png,.jpg,.jpeg,.pdf"
              onMultipleUpload={handleFileUpload}
              onRemove={handleFileRemove}
              handleDownloadLink={handleDownload}
              small={true}
              testId="add-attachment-modal-upload-supplementary-docs"
            />
          </div>
        ),
        cancelButtonText: "Cancel",
        confirmButtonAction: handleAddAttachment,
        confirmButtonDisable: attachments.length === 0,
        confirmButtonText: "Confirm",
        confirmButtonType: "primary",
        title: `Add Attachment: ${selectedExceptionIds.length} ${selectedExceptionIds.length>1 ? 'Exceptions':'Exception'} Selected`,
      }}
      isVisible={isModalOpen}
      hideBodyScroll={true}
    />
  );
};

const AddAttachmentModal: FC<AddAttachmentModalProps> = (props: AddAttachmentModalProps) => (
  <DragAndDropMultiFileUploaderContextProvider>
    <WrappedAddAttachmentModal {...props} />
  </DragAndDropMultiFileUploaderContextProvider>
);

export default AddAttachmentModal;
